import { Link } from '@tanstack/react-router';
import { Ban } from 'lucide-react';
import { documentViewSearch } from '../../../router';
import { usePublicationRejectedSubmissions } from '../hooks/usePublicationLists.js';

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export function RejectedSubmissionsPage() {
  const { data, isLoading, error } = usePublicationRejectedSubmissions();

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Documents refusés</h1>
        <p className="mt-1 text-slate-500">
          Dernière soumission refusée : consultez les remarques des validateurs, corrigez le
          document puis soumettez à nouveau.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <span className="relative flex h-10 w-10" aria-hidden="true">
            <span className="absolute inset-0 rounded-full border-[3px] border-slate-200" />
            <span className="absolute inset-0 animate-spin rounded-full border-[3px] border-transparent border-t-emerald-500" />
          </span>
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Impossible de charger la liste des documents refusés.
        </div>
      ) : data && data.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {data.map((row) => (
            <li key={row.submitId}>
              <Link
                to="/documents/$id"
                params={{ id: row.documentId }}
                search={documentViewSearch.closed}
                className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm transition-shadow hover:border-rose-200 hover:shadow-md"
              >
                <div>
                  <p className="font-medium text-slate-900">{row.documentName}</p>
                  <p className="mt-1 text-sm text-slate-500">
                    Soumission du {formatWhen(row.submittedAt)} par{' '}
                    <span className="font-medium text-slate-600">{row.submittedByAlias}</span>
                    {' · '}
                    <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                      {row.extension}
                    </span>
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold text-rose-700">
                    {row.rejectedValidationCount} bloc
                    {row.rejectedValidationCount > 1 ? 's' : ''} refusé
                    {row.rejectedValidationCount > 1 ? 's' : ''}
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-col items-center gap-4 rounded-2xl border-2 border-dashed border-slate-200 py-20">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
            <Ban className="h-7 w-7 text-slate-400" />
          </div>
          <p className="text-center font-medium text-slate-700">Aucun document avec refus récent</p>
          <p className="max-w-md text-center text-sm text-slate-500">
            Lorsque toutes les validations d’une soumission sont traitées et qu’au moins une est
            refusée, le document réapparaît ici jusqu’à une nouvelle soumission.
          </p>
        </div>
      )}
    </div>
  );
}
